import { getStageName, getServices, getServiceName, getServiceQty, getServiceToothNumber, isExecuted } from './constructorTypes'
import type { ConstructorPlan, ConstructorStage } from './constructorTypes'

export const constructorPrintStyle = `
  @media print {
    body * { visibility: hidden !important; }
    .print-only { visibility: visible !important; display: block !important; position: absolute !important; top: 0 !important; left: 0 !important; width: 100% !important; z-index: 99999 !important; background: white !important; padding: 0 !important; }
    .print-only * { visibility: visible !important; }
    body { background: white; margin: 0; }
    @page { margin: 15mm 15mm 15mm 15mm; size: A4; }
  }
  .print-only { display: none; }
  .print-table { width: 100%; border-collapse: collapse; font-size: 11px; }
  .print-table th { background: #f1f5f9; border: 1px solid #cbd5e1; padding: 5px 8px; text-align: left; font-weight: 600; }
  .print-table td { border: 1px solid #e2e8f0; padding: 4px 8px; vertical-align: top; }
  .print-table td.num { text-align: right; white-space: nowrap; }
  .print-table td.center { text-align: center; }
  .print-stage { margin-bottom: 18px; page-break-inside: avoid; }
  .print-stage-header { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; padding: 6px 10px; margin-bottom: 6px; }
  .print-stage-title { font-size: 13px; font-weight: 700; color: #1e293b; }
  .print-stage-meta { font-size: 10px; color: #64748b; margin-top: 2px; }
  .print-stage-comment { font-size: 10px; color: #475569; margin-top: 4px; font-style: italic; }
  .print-section { margin-bottom: 14px; }
  .print-label { font-size: 10px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 3px; }
  .print-value { font-size: 12px; color: #1e293b; }
  .print-divider { border: none; border-top: 1px solid #e2e8f0; margin: 12px 0; }
  .print-total-row { background: #f1f5f9; font-weight: 700; }
  .print-installment-table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 6px; }
  .print-installment-table th { background: #f1f5f9; border: 1px solid #cbd5e1; padding: 4px 8px; font-weight: 600; text-align: left; }
  .print-installment-table td { border: 1px solid #e2e8f0; padding: 3px 8px; }
`

export function ConstructorPrintView({
  planDetail,
  stages,
  totalAmount,
}: {
  planDetail: ConstructorPlan
  stages: ConstructorStage[]
  totalAmount: number
}) {
  return (
    <div className="print-only" style={{ fontFamily: 'Arial, sans-serif', color: '#1e293b', padding: '0' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, paddingBottom: 10, borderBottom: '2px solid #1e293b' }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}>{planDetail.title || 'План лечения'}</div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>ID плана: #{planDetail.id}</div>
        </div>
        <div style={{ textAlign: 'right', fontSize: 10, color: '#64748b' }}>
          <div>Дата печати: {new Date().toLocaleDateString('ru-RU')}</div>
          {planDetail.created_at && <div>Создан: {new Date(planDetail.created_at).toLocaleDateString('ru-RU')}</div>}
        </div>
      </div>

      {/* Plan info */}
      <div style={{ display: 'flex', gap: 32, marginBottom: 12, flexWrap: 'wrap' }}>
        {planDetail.patient_name && (
          <div className="print-section">
            <div className="print-label">Пациент</div>
            <div className="print-value" style={{ fontWeight: 600 }}>{planDetail.patient_name}</div>
          </div>
        )}
        {planDetail.doctor_name && (
          <div className="print-section">
            <div className="print-label">Лечащий врач</div>
            <div className="print-value">{planDetail.doctor_name}</div>
          </div>
        )}
        <div className="print-section">
          <div className="print-label">Статус</div>
          <div className="print-value">{planDetail.status === 'active' ? 'Активный' : planDetail.status ?? '—'}</div>
        </div>
        {planDetail.offer_deadline && (
          <div className="print-section">
            <div className="print-label">Цена актуальна до</div>
            <div className="print-value" style={{ color: '#b45309', fontWeight: 600 }}>
              {new Date(planDetail.offer_deadline).toLocaleDateString('ru-RU')}
            </div>
          </div>
        )}
      </div>

      {planDetail.note && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4, padding: '6px 10px', marginBottom: 12, fontSize: 11, color: '#475569', fontStyle: 'italic' }}>
          {planDetail.note}
        </div>
      )}

      <hr className="print-divider" />

      {/* Stages */}
      {stages.map((stage, stageIdx) => {
        const svcs = getServices(stage)
        const stageTotal = svcs.reduce((sum, svc) => {
          const unit = svc.unit_price ?? svc.price ?? 0
          const qty = getServiceQty(svc)
          return sum + (svc.total_price ?? unit * qty)
        }, 0)
        return (
          <div key={stage.id} className="print-stage">
            <div className="print-stage-header">
              <div className="print-stage-title">{stageIdx + 1}. {getStageName(stage)}</div>
              {stage.doctor_name && (
                <div className="print-stage-meta">Врач этапа: {stage.doctor_name}</div>
              )}
              {stage.description && (
                <div className="print-stage-comment">{stage.description}</div>
              )}
            </div>
            <table className="print-table">
              <thead>
                <tr>
                  <th style={{ width: '45%' }}>Услуга</th>
                  <th className="center" style={{ width: '7%' }}>Зуб</th>
                  <th className="center" style={{ width: '7%' }}>Кол.</th>
                  <th style={{ width: '15%', textAlign: 'right' }}>Цена</th>
                  <th style={{ width: '15%', textAlign: 'right' }}>Сумма</th>
                  <th className="center" style={{ width: '6%' }}>✓</th>
                </tr>
              </thead>
              <tbody>
                {svcs.map(svc => {
                  const unitP = svc.unit_price ?? svc.price ?? 0
                  const qty = getServiceQty(svc)
                  const totalP = svc.total_price ?? unitP * qty
                  return (
                    <tr key={svc.id}>
                      <td>{getServiceName(svc)}</td>
                      <td className="center">{getServiceToothNumber(svc) || '—'}</td>
                      <td className="center">{qty}</td>
                      <td className="num">{unitP > 0 ? `${Number(unitP).toLocaleString('ru-RU')} ₽` : '—'}</td>
                      <td className="num">{totalP > 0 ? `${Number(totalP).toLocaleString('ru-RU')} ₽` : '—'}</td>
                      <td className="center">{isExecuted(svc) ? '✓' : ''}</td>
                    </tr>
                  )
                })}
                <tr className="print-total-row">
                  <td colSpan={4} style={{ textAlign: 'right', fontSize: 11, fontWeight: 700 }}>Итого по этапу:</td>
                  <td className="num">{stageTotal.toLocaleString('ru-RU')} ₽</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )
      })}

      <hr className="print-divider" />

      {/* Summary */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: '#64748b' }}>Этапов: {stages.length} · Услуг: {stages.flatMap(getServices).length}</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>Итого: {totalAmount.toLocaleString('ru-RU')} ₽</div>
      </div>

      {/* Cash offer */}
      {planDetail.plan_cash_offer && (planDetail.plan_cash_offer.amount != null || planDetail.plan_cash_offer.note) && (
        <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: 4, padding: '6px 10px', marginBottom: 12, fontSize: 11 }}>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>Спецусловия за наличные</div>
          {planDetail.plan_cash_offer.amount != null && (
            <div>Сумма: <strong>{Number(planDetail.plan_cash_offer.amount).toLocaleString('ru-RU')} ₽</strong></div>
          )}
          {planDetail.plan_cash_offer.deadline && <div>Срок: {planDetail.plan_cash_offer.deadline}</div>}
          {planDetail.plan_cash_offer.note && <div>{planDetail.plan_cash_offer.note}</div>}
        </div>
      )}

      {/* Installment */}
      {planDetail.installment?.enabled && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 4, padding: '8px 10px', marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Рассрочка</div>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 11, marginBottom: 6 }}>
            {planDetail.installment.amount != null && <span>Сумма: <strong>{Number(planDetail.installment.amount).toLocaleString('ru-RU')} ₽</strong></span>}
            {planDetail.installment.months != null && <span>Срок: <strong>{planDetail.installment.months} мес.</strong></span>}
            {planDetail.installment.rate != null && planDetail.installment.rate > 0 && <span>Ставка: <strong>{planDetail.installment.rate}%</strong></span>}
            {planDetail.installment.start_date && <span>Старт: <strong>{new Date(planDetail.installment.start_date).toLocaleDateString('ru-RU')}</strong></span>}
          </div>
          {planDetail.installment.note && <div style={{ fontSize: 10, color: '#64748b', fontStyle: 'italic', marginBottom: 6 }}>{planDetail.installment.note}</div>}
          {planDetail.installment.schedule && planDetail.installment.schedule.length > 0 && (
            <table className="print-installment-table">
              <thead>
                <tr>
                  <th>Платёж</th>
                  <th>Сумма</th>
                  <th>Дата</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody>
                {planDetail.installment.schedule.map((pay, i) => (
                  <tr key={pay.id ?? i}>
                    <td>{pay.title || `Платёж ${i + 1}`}</td>
                    <td>{pay.amount != null ? `${Number(pay.amount).toLocaleString('ru-RU')} ₽` : '—'}</td>
                    <td>{pay.due_date ? new Date(pay.due_date).toLocaleDateString('ru-RU') : '—'}</td>
                    <td>{pay.status ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Footer */}
      <div style={{ marginTop: 20, paddingTop: 8, borderTop: '1px solid #e2e8f0', fontSize: 9, color: '#94a3b8', textAlign: 'right' }}>
        Документ сформирован {new Date().toLocaleString('ru-RU')}
      </div>
    </div>
  )
}
